import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { callApi } from '../api';
import siteLogo from '../assets/logo.png';

function MobileHeader({ kulup, pageName }) {
    const navigate = useNavigate();
    const [showNotifs, setShowNotifs] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowNotifs(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch notifications from API data
    const fetchNotifications = useCallback(async () => {
        const notifs = [];
        try {
            // Pending applications
            const appsRes = await callApi('/basvurular', 'GET');
            if (appsRes?.items) {
                const pending = appsRes.items.filter(b => b.durum === 'BEKLEMEDE');
                if (pending.length > 0) {
                    notifs.push({
                        id: 'pending-apps',
                        type: 'warning',
                        text: `${pending.length} yeni başvuru onay bekliyor`,
                        time: pending[0]?.basvuruTarihi || new Date().toISOString(),
                        route: '/basvurular',
                        icon: '📋'
                    });
                }
            }

            // Recent members
            const membersRes = await callApi('/uyeler', 'GET');
            if (membersRes?.items) {
                const recent = membersRes.items
                    .filter(m => m.kayitTarihi)
                    .sort((a, b) => new Date(b.kayitTarihi) - new Date(a.kayitTarihi))
                    .slice(0, 3);

                recent.forEach(m => {
                    const daysDiff = (Date.now() - new Date(m.kayitTarihi).getTime()) / 86400000;
                    if (daysDiff < 7) {
                        notifs.push({
                            id: `member-${m.uyeID}`,
                            type: 'info',
                            text: `${m.ad} ${m.soyad} üye olarak kaydoldu`,
                            time: m.kayitTarihi,
                            route: '/uyeler',
                            icon: '👤'
                        });
                    }
                });
            }

            // Recent audit logs
            const logsRes = await callApi('/admin/audit-logs', 'GET');
            if (logsRes?.logs) {
                const importantLogs = logsRes.logs
                    .filter(l => l.action === 'POST' || l.details?.includes('Login'))
                    .slice(0, 3);

                importantLogs.forEach(l => {
                    notifs.push({
                        id: `log-${l.id}`,
                        type: 'default',
                        text: `${l.userCode || 'Sistem'}: ${l.details || l.endpoint}`,
                        time: l.createdAt,
                        route: '/denetim',
                        icon: '🔔'
                    });
                });
            }
        } catch (err) {
            console.warn('Bildirim verisi yüklenemedi:', err);
        }

        // Check read state from localStorage
        const readIds = JSON.parse(localStorage.getItem('readNotifs') || '[]');
        const enriched = notifs.map(n => ({
            ...n,
            read: readIds.includes(n.id)
        }));

        setNotifications(enriched);
        setUnreadCount(enriched.filter(n => !n.read).length);
    }, []);

    useEffect(() => {
        fetchNotifications();
        // Refresh every 2 minutes
        const interval = setInterval(fetchNotifications, 120000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    const handleNotifClick = (notif) => {
        // Mark as read
        const readIds = JSON.parse(localStorage.getItem('readNotifs') || '[]');
        if (!readIds.includes(notif.id)) {
            readIds.push(notif.id);
            localStorage.setItem('readNotifs', JSON.stringify(readIds));
        }

        setShowNotifs(false);
        navigate(notif.route);
    };

    const markAllRead = () => {
        const allIds = notifications.map(n => n.id);
        localStorage.setItem('readNotifs', JSON.stringify(allIds));
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadCount(0);
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffMin = Math.floor(diffMs / 60000);
        const diffHour = Math.floor(diffMs / 3600000);
        const diffDay = Math.floor(diffMs / 86400000);

        if (diffMin < 1) return 'Az önce';
        if (diffMin < 60) return `${diffMin} dk önce`;
        if (diffHour < 24) return `${diffHour} saat önce`;
        if (diffDay < 7) return `${diffDay} gün önce`;
        return date.toLocaleDateString('tr-TR');
    };

    return (
        <header className="mobile-header">
            <div className="header-left">
                <img src={siteLogo} alt="Logo" className="header-logo" style={{ objectFit: 'contain', background: 'transparent' }} />
                <span className="header-title">{pageName || kulup?.ad || 'KluGastro'}</span>
            </div>

            <div className="header-right" ref={dropdownRef}>
                <button
                    className="header-notification-btn"
                    onClick={() => setShowNotifs(!showNotifs)}
                    aria-label="Bildirimler"
                >
                    🔔
                    {unreadCount > 0 && (
                        <span className="notification-badge">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </button>

                {showNotifs && (
                    <div className="notification-dropdown">
                        <div className="notification-header">
                            <span>Bildirimler</span>
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllRead}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#6366f1',
                                        fontSize: '0.78rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        minHeight: 'auto'
                                    }}
                                >
                                    Tümünü okundu yap
                                </button>
                            )}
                        </div>

                        {notifications.length === 0 ? (
                            <div className="notification-empty">
                                Yeni bildirim yok 🎉
                            </div>
                        ) : (
                            <div style={{ overflowY: 'auto', maxHeight: '320px' }}>
                                {notifications.map(notif => (
                                    <div
                                        key={notif.id}
                                        className={`notification-item ${!notif.read ? 'unread' : ''}`}
                                        onClick={() => handleNotifClick(notif)}
                                    >
                                        {!notif.read && <div className="notification-dot"></div>}
                                        <div className="notification-content">
                                            <div className="notification-text">
                                                {notif.icon} {notif.text}
                                            </div>
                                            <div className="notification-time">
                                                {formatTime(notif.time)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </header>
    );
}

export default MobileHeader;
